import React, { useState, useMemo } from 'react';
import {
  Box,
  Switch,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { IconPencil, IconTrash, IconSearch } from '@tabler/icons-react';
import type { CustomerAlias, Vendor } from '@techgitter/contracts';
import { AliasType, ALIAS_TYPE_LABELS } from '@techgitter/contracts';
import {
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../table';
import { Chip, type ChipTone } from '../chip';
import { SmartSelect, type SmartSelectOption } from '../dropdown';
import { FIELD_SX } from '../../styles/designSystem';
import type { PanelDensity } from '../tickets/Customer/panelStyles';

/** Alias-type → design-system chip tone (replaces the MUI primary/secondary map). */
const ALIAS_TYPE_TONE: Record<number, ChipTone> = {
  [AliasType.VendorMatching]: 'info',
  [AliasType.GlobalMatching]: 'info',
  [AliasType.ReferenceOnly]: 'neutral',
};

const TYPE_OPTIONS: SmartSelectOption[] = Object.entries(ALIAS_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

const ACTIVE_OPTIONS: SmartSelectOption[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

interface AliasTableProps {
  aliases: CustomerAlias[];
  vendors: Vendor[];
  canEditMatchingAliases: boolean;
  canDeleteAliases: boolean;
  canToggleActive: boolean;
  onEdit: (alias: CustomerAlias) => void;
  onToggleActive: (aliasId: string, isActive: boolean) => Promise<void>;
  onDelete: (aliasId: string) => void;
  /** Density tier — `comfortable` (customer-detail card) shows the full filter
   *  row + all columns; `compact` (default; New Ticket sidebar) shows search
   *  only + a reduced column set so it fits a narrow column. */
  density?: PanelDensity;
}

export const AliasTable: React.FC<AliasTableProps> = ({
  aliases,
  vendors,
  canEditMatchingAliases,
  canDeleteAliases,
  canToggleActive,
  onEdit,
  onToggleActive,
  onDelete,
  density = 'compact',
}) => {
  const isComfortable = density === 'comfortable';
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<AliasType | ''>('');
  const [vendorFilter, setVendorFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  const filteredAliases = useMemo(() => {
    let result = aliases;

    if (search.trim()) {
      const term = search.trim().toLowerCase();
      result = result.filter(
        (a) =>
          a.alias.toLowerCase().includes(term) ||
          (a.aliasNormalized && a.aliasNormalized.toLowerCase().includes(term))
      );
    }

    if (typeFilter !== '') {
      result = result.filter((a) => a.aliasType === typeFilter);
    }

    if (vendorFilter) {
      result = result.filter((a) => a.vendorId === vendorFilter);
    }

    if (activeFilter === 'active') {
      result = result.filter((a) => a.isActive);
    } else if (activeFilter === 'inactive') {
      result = result.filter((a) => !a.isActive);
    }

    return result;
  }, [aliases, search, typeFilter, vendorFilter, activeFilter]);

  const handleToggleActive = async (alias: CustomerAlias) => {
    setTogglingIds((prev) => new Set(prev).add(alias.aliasId));
    try {
      await onToggleActive(alias.aliasId, !alias.isActive);
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(alias.aliasId);
        return next;
      });
    }
  };

  const vendorMap = useMemo(() => {
    const map = new Map<string, string>();
    vendors.forEach((v) => map.set(v.vendorId, v.name));
    return map;
  }, [vendors]);

  // Only show vendor filter options that actually appear in aliases
  const vendorFilterOptions = useMemo(() => {
    const vendorIds = new Set(aliases.map((a) => a.vendorId).filter(Boolean) as string[]);
    return vendors.filter((v) => vendorIds.has(v.vendorId));
  }, [aliases, vendors]);

  const vendorOptions: SmartSelectOption[] = useMemo(
    () => vendorFilterOptions.map((v) => ({ value: v.vendorId, label: v.name })),
    [vendorFilterOptions],
  );

  // Comfortable shows the Vendor + Normalised columns; compact drops them.
  const colCount = isComfortable ? 6 : 4;

  return (
    <Box>
      {/* Filter bar — search always; the dropdowns only at comfortable density. */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search aliases…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconSearch size={16} stroke={1.5} />
              </InputAdornment>
            ),
          }}
          sx={[FIELD_SX, { width: isComfortable ? 220 : '100%' }]}
        />

        {isComfortable && (
          <>
            <Box sx={{ width: 160 }}>
              <SmartSelect
                label="Type"
                placeholder="All types"
                value={typeFilter === '' ? null : String(typeFilter)}
                options={TYPE_OPTIONS}
                onChange={(v) => setTypeFilter(v ? (Number(v) as AliasType) : '')}
              />
            </Box>
            {vendorOptions.length > 0 && (
              <Box sx={{ width: 160 }}>
                <SmartSelect
                  label="Vendor"
                  placeholder="All vendors"
                  value={vendorFilter || null}
                  options={vendorOptions}
                  onChange={(v) => setVendorFilter(v ?? '')}
                />
              </Box>
            )}
            <Box sx={{ width: 130 }}>
              <SmartSelect
                label="Status"
                value={activeFilter}
                options={ACTIVE_OPTIONS}
                onChange={(v) => setActiveFilter((v as typeof activeFilter) ?? 'all')}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Table */}
      {filteredAliases.length === 0 ? (
        <DataTable>
          <tbody>
            <DataTableEmpty colSpan={colCount}>
              {aliases.length === 0
                ? 'No aliases configured for this customer.'
                : 'No aliases match the current filters.'}
            </DataTableEmpty>
          </tbody>
        </DataTable>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
        <DataTable>
          <thead>
            <tr>
              <DataTableHeadCell>Alias</DataTableHeadCell>
              <DataTableHeadCell>Type</DataTableHeadCell>
              {isComfortable && <DataTableHeadCell>Vendor</DataTableHeadCell>}
              {isComfortable && <DataTableHeadCell>Normalised</DataTableHeadCell>}
              <DataTableHeadCell align="center">Active</DataTableHeadCell>
              <DataTableHeadCell align="right">Actions</DataTableHeadCell>
            </tr>
          </thead>
          <tbody>
            {filteredAliases.map((alias) => (
              <DataTableRow key={alias.aliasId}>
                <DataTableCell
                  bold
                  style={{ opacity: alias.isActive ? 1 : 0.55 }}
                >
                  {alias.alias}
                </DataTableCell>
                <DataTableCell style={{ opacity: alias.isActive ? 1 : 0.55 }}>
                  <Chip tone={ALIAS_TYPE_TONE[alias.aliasType] ?? 'neutral'}>
                    {ALIAS_TYPE_LABELS[alias.aliasType as AliasType] || alias.aliasTypeLabel}
                  </Chip>
                </DataTableCell>
                {isComfortable && (
                  <DataTableCell muted style={{ opacity: alias.isActive ? 1 : 0.55 }}>
                    {alias.vendorId
                      ? alias.vendorName || vendorMap.get(alias.vendorId) || alias.vendorId
                      : '—'}
                  </DataTableCell>
                )}
                {isComfortable && (
                  <DataTableCell
                    muted
                    style={{ fontFamily: 'monospace', opacity: alias.isActive ? 1 : 0.55 }}
                  >
                    {alias.aliasNormalized || '—'}
                  </DataTableCell>
                )}
                <DataTableCell align="center">
                  <Switch
                    size="small"
                    checked={alias.isActive}
                    disabled={!canToggleActive || togglingIds.has(alias.aliasId)}
                    onChange={() => handleToggleActive(alias)}
                  />
                </DataTableCell>
                <DataTableCell align="right">
                  {canEditMatchingAliases && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => onEdit(alias)}>
                        <IconPencil size={16} stroke={1.5} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDeleteAliases && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDelete(alias.aliasId)}
                      >
                        <IconTrash size={16} stroke={1.5} />
                      </IconButton>
                    </Tooltip>
                  )}
                </DataTableCell>
              </DataTableRow>
            ))}
          </tbody>
        </DataTable>
        </Box>
      )}
    </Box>
  );
};
